'use client';
import { useState, useEffect } from 'react';
import { CardItemType } from '../type';

type CardEntry = {
  id: string;
  content: CardItemType;
  randomRotate: number;
};

type Options = {
  cards: CardItemType[];
  autoplay?: boolean;
  autoplayDelay?: number;
};

const toEntries = (cards: CardItemType[]): CardEntry[] =>
  cards.map((card) => ({ id: card.id, content: card, randomRotate: 0 }));

export const useCardStack = ({
  cards,
  autoplay = false,
  autoplayDelay = 3000,
}: Options) => {
  const [stack, setStack] = useState<CardEntry[]>(() => toEntries(cards));
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    setStack(toEntries(cards));
  }, [cards]);

  const sendToBack = (id: string) => {
    setStack((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      const next = [...prev];
      const [card] = next.splice(idx, 1);
      next.unshift(card!);
      return next;
    });
  };

  const next = () =>
    setStack((prev) => {
      if (prev.length <= 1) return prev;
      const top = prev[prev.length - 1]!;
      return [top, ...prev.slice(0, -1)];
    });

  const prev = () =>
    setStack((prev) => {
      if (prev.length <= 1) return prev;
      const [bottom, ...rest] = prev;
      return [...rest, bottom!];
    });

  useEffect(() => {
    if (!autoplay || isPaused) return;
    const id = setInterval(next, autoplayDelay);
    return () => clearInterval(id);
  }, [autoplay, autoplayDelay, isPaused]);

  return { stack, sendToBack, next, prev, setIsPaused };
};
